interface TutorialStep {
  message: string;
  highlight?: string; // CSS selector to highlight
  waitFor: 'click' | 'upgrade' | 'delay';
  delay?: number;
}

const STEPS: TutorialStep[] = [
  {
    message: "Welcome, Archon. You are an immortal ruler.\nYour destiny: build a civilization across the ages.",
    waitFor: 'click',
  },
  {
    message: "Start by building a Mine.\nIt will produce Gold automatically.",
    highlight: '#building-panel',
    waitFor: 'upgrade',
  },
  {
    message: "Your Mine is producing Gold!\nWatch the numbers grow in the top bar.",
    waitFor: 'delay',
    delay: 3000,
  },
  {
    message: "Now build a Farm to produce Food.\nYou need Food to launch Raids later.",
    waitFor: 'upgrade',
  },
  {
    message: "Each building produces resources every second.\nUpgrade them to produce more.",
    waitFor: 'click',
  },
  {
    message: "When you're powerful enough, you can Prestige\nto reset and gain permanent multipliers.",
    waitFor: 'click',
  },
  {
    message: "Raid other Archons for bonus loot.\nCheck the Shop for gems.\n\nGood luck, Archon. Build your Eternal Throne.",
    waitFor: 'click',
  },
];

export class Tutorial {
  private container: HTMLElement;
  private messageEl: HTMLElement;
  private tapHint: HTMLElement;
  private currentStep: number = 0;
  private onComplete: () => void;
  private upgradeResolver: (() => void) | null = null;
  private active: boolean = true;

  constructor(parent: HTMLElement, onComplete: () => void) {
    this.onComplete = onComplete;

    this.container = document.createElement('div');
    this.container.id = 'tutorial';
    this.container.style.cssText = `
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      z-index: 200; display: flex; align-items: center; justify-content: center;
      background: rgba(0, 0, 0, 0.6);
      touch-action: manipulation;
    `;

    const box = document.createElement('div');
    box.style.cssText = `
      background: rgba(15, 10, 30, 0.95);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(201, 164, 76, 0.5);
      border-radius: 12px;
      padding: 24px 28px;
      max-width: 320px;
      text-align: center;
      box-shadow: 0 8px 40px rgba(0,0,0,0.7), 0 0 30px rgba(201,164,76,0.1);
      animation: panelIn 0.3s ease-out;
    `;

    this.messageEl = document.createElement('div');
    this.messageEl.style.cssText = `
      font-family: 'Segoe UI', system-ui, sans-serif;
      font-size: 15px;
      line-height: 1.6;
      color: #d4d0c8;
      white-space: pre-line;
      margin-bottom: 16px;
    `;

    this.tapHint = document.createElement('div');
    this.tapHint.style.cssText = `
      font-family: 'Segoe UI', system-ui, sans-serif;
      font-size: 11px;
      color: #6b6570;
      animation: pulse 1.5s infinite;
    `;
    this.tapHint.textContent = 'tap to continue';

    // Add pulse animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
    `;
    document.head.appendChild(style);

    box.appendChild(this.messageEl);
    box.appendChild(this.tapHint);
    this.container.appendChild(box);

    this.container.addEventListener('click', () => this.handleTap());

    parent.appendChild(this.container);
    this.showStep();
  }

  private showStep(): void {
    if (this.currentStep >= STEPS.length) {
      this.complete();
      return;
    }

    const step = STEPS[this.currentStep]!;
    this.messageEl.textContent = step.message;

    if (step.waitFor === 'upgrade') {
      this.tapHint.textContent = '(upgrade a building to continue)';
      this.container.style.pointerEvents = 'none';
      this.container.style.background = 'rgba(0, 0, 0, 0.3)';
    } else if (step.waitFor === 'delay') {
      this.tapHint.textContent = '';
      this.container.style.pointerEvents = 'none';
      setTimeout(() => this.nextStep(), step.delay ?? 2000);
    } else {
      this.tapHint.textContent = 'tap to continue';
      this.container.style.pointerEvents = 'auto';
      this.container.style.background = 'rgba(0, 0, 0, 0.6)';
    }
  }

  private handleTap(): void {
    if (!this.active) return;
    const step = STEPS[this.currentStep];
    if (step?.waitFor === 'click') {
      this.nextStep();
    }
  }

  notifyUpgrade(): void {
    if (!this.active) return;
    const step = STEPS[this.currentStep];
    if (step?.waitFor === 'upgrade') {
      this.nextStep();
    }
  }

  private nextStep(): void {
    this.currentStep++;
    this.showStep();
  }

  private complete(): void {
    this.active = false;
    this.container.style.opacity = '0';
    this.container.style.transition = 'opacity 0.3s';
    setTimeout(() => {
      this.container.remove();
      this.onComplete();
    }, 300);
  }

  isActive(): boolean {
    return this.active;
  }

  destroy(): void {
    this.active = false;
    this.container.remove();
  }
}
